import { useCallback, useEffect, useRef } from 'react';
const NATIVE_ERROR_CODE_TO_REASON = {
    1: 'denied',
    2: 'positionUnavailable',
    3: 'timeout'
};
const classifyNativeError = (code) => (NATIVE_ERROR_CODE_TO_REASON[code] ?? 'unavailable');
const useGeolocationAcquisition = ({ cacheStore, errorReporter, timeoutMs, maximumAgeMs, setGeolocationStatus, setIsGettingLocation }) => {
    const activeCancelsRef = useRef(new Set());
    useEffect(() => () => {
        const cancels = activeCancelsRef.current;
        while (cancels.size > 0) {
            const [cancel] = cancels;
            cancels.delete(cancel);
            cancel();
        }
    }, []);
    const acquire = useCallback((onPermisonsApproved, onPermisonsRejected) => {
        let settled = false;
        let hasRetried = false;
        let generation = 0;
        let watchId = null;
        let timeoutId;
        const stopWatching = () => {
            clearTimeout(timeoutId);
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
            }
        };
        let cancel;
        const settle = () => {
            settled = true;
            stopWatching();
            activeCancelsRef.current.delete(cancel);
        };
        cancel = () => {
            if (settled)
                return;
            settle();
        };
        activeCancelsRef.current.add(cancel);
        const attempt = () => {
            generation += 1;
            const attemptGeneration = generation;
            timeoutId = setTimeout(() => {
                if (settled)
                    return;
                settle();
                onPermisonsRejected('timeout');
                setIsGettingLocation(false);
            }, timeoutMs);
            const nativeWatchId = navigator.geolocation.watchPosition(async (position) => {
                if (settled || attemptGeneration !== generation)
                    return;
                settle();
                cacheStore.write(position.coords);
                const approvalResult = onPermisonsApproved(position.coords);
                const resolvedApprovalResult = approvalResult instanceof Promise
                    ? await approvalResult
                    : approvalResult;
                if (resolvedApprovalResult !== false) {
                    setGeolocationStatus('granted');
                }
                setIsGettingLocation(false);
            }, (error) => {
                if (settled || attemptGeneration !== generation)
                    return;
                errorReporter.warning(new Error(`Geolocation error: code=${error.code} message=${error.message}`));
                const reason = classifyNativeError(error.code);
                if (reason === 'positionUnavailable' && !hasRetried) {
                    hasRetried = true;
                    stopWatching();
                    attempt();
                    return;
                }
                settle();
                onPermisonsRejected(reason);
                if (reason === 'denied') {
                    setGeolocationStatus('denied');
                }
                setIsGettingLocation(false);
            }, { timeout: timeoutMs, maximumAge: maximumAgeMs });
            if (settled || attemptGeneration !== generation) {
                navigator.geolocation.clearWatch(nativeWatchId);
                return;
            }
            watchId = nativeWatchId;
        };
        attempt();
    }, [
        cacheStore,
        errorReporter,
        timeoutMs,
        maximumAgeMs,
        setGeolocationStatus,
        setIsGettingLocation
    ]);
    const getCurrentPosition = useCallback((onPermisonsApproved, onPermisonsRejected) => {
        const cachedCoords = cacheStore.read();
        if (cachedCoords) {
            setGeolocationStatus('granted');
            setIsGettingLocation(false);
            const approvalResult = onPermisonsApproved(cachedCoords);
            if (approvalResult instanceof Promise) {
                approvalResult.catch(() => undefined);
            }
            return;
        }
        if (!navigator?.geolocation) {
            onPermisonsRejected('unsupported');
            setGeolocationStatus('denied');
            setIsGettingLocation(false);
            return;
        }
        setIsGettingLocation(true);
        acquire(onPermisonsApproved, onPermisonsRejected);
    }, [cacheStore, setGeolocationStatus, setIsGettingLocation, acquire]);
    return { getCurrentPosition };
};
export default useGeolocationAcquisition;
